/**
 * Decision Records service
 *
 * Reads the artifacts produced by the Ambient Runtime judgment-lane jobs:
 *   - decision_narratives     (AMB-4)  "who decided, and why"
 *   - evidence_briefs         (AMB-5)  "what changed since ratification"
 *   - outcome_retrospectives  (AMB-7)  "how the ratified list actually landed"
 *
 * These tables are written server-side by the `ambient` edge function; this is
 * the read side that surfaces them in the app (see pages/DecisionRecords.tsx).
 */

import { getSupabaseClient } from './supabaseClient';

export type DecisionRecordKind =
  | 'decision_narrative'
  | 'evidence_brief'
  | 'outcome_retrospective';

export interface DecisionRecord {
  id: string;
  kind: DecisionRecordKind;
  title: string;
  summary: string | null;
  markdown: string;
  date: string | null;
}

/** Pull the first markdown H1 as a title, else fall back. */
function deriveTitle(markdown: string | null | undefined, fallback: string): string {
  const m = (markdown ?? '').match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : fallback;
}

export async function fetchDecisionRecords(
  workspaceId: string,
): Promise<{ data: DecisionRecord[]; error: string | null }> {
  const supabase = getSupabaseClient();
  try {
    const [nar, brf, ret] = await Promise.all([
      supabase
        .from('decision_narratives')
        .select('id, title, executive_summary, brief_markdown, approved_at, generated_at')
        .eq('workspace_id', workspaceId)
        .order('generated_at', { ascending: false }),
      supabase
        .from('evidence_briefs')
        .select('id, brief_md, summary, payload, created_at')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false }),
      supabase
        .from('outcome_retrospectives')
        .select('id, retrospective_md, summary, created_at')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false }),
    ]);

    const records: DecisionRecord[] = [];

    for (const r of (nar.data ?? []) as Record<string, unknown>[]) {
      records.push({
        id: r.id as string,
        kind: 'decision_narrative',
        title: ((r.title as string) ?? '').trim() || deriveTitle(r.brief_markdown as string, 'Decision narrative'),
        summary: (r.executive_summary as string) ?? null,
        markdown: (r.brief_markdown as string) ?? '',
        date: (r.generated_at as string) ?? (r.approved_at as string) ?? null,
      });
    }
    for (const r of (brf.data ?? []) as Record<string, unknown>[]) {
      const payload = (r.payload ?? {}) as Record<string, unknown>;
      records.push({
        id: r.id as string,
        kind: 'evidence_brief',
        title: deriveTitle(r.brief_md as string, (payload.momentHeadline as string) || 'Evidence brief'),
        summary: (r.summary as string) ?? null,
        markdown: (r.brief_md as string) ?? '',
        date: (r.created_at as string) ?? null,
      });
    }
    for (const r of (ret.data ?? []) as Record<string, unknown>[]) {
      records.push({
        id: r.id as string,
        kind: 'outcome_retrospective',
        title: deriveTitle(r.retrospective_md as string, 'Outcome retrospective'),
        summary: (r.summary as string) ?? null,
        markdown: (r.retrospective_md as string) ?? '',
        date: (r.created_at as string) ?? null,
      });
    }

    records.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
    const error = nar.error?.message ?? brf.error?.message ?? ret.error?.message ?? null;
    return { data: records, error };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Unknown error' };
  }
}
